// Central API service for handling requests with centralized configuration 
// MODIFICADO - Solo usar token de Keycloak via externalTokenProvider

import apiConfig, { 
  // COMENTADO - Solo usar token de Keycloak via externalTokenProvider
  // getAuthToken, 
  // hasValidToken, 
  buildEndpointUrl,
  PROXY_CONFIG,
  LOGGING_CONFIG 
} from 'config/apiConfig';
import { appConfig } from 'config/keycloak';

class ApiService {
  constructor() {
    this.baseUrl = appConfig.baseUrl;
    this.config = apiConfig;
    // Optional external token provider (e.g., Keycloak via AuthContext)
    // Function may return string or Promise<string>
    this.externalTokenProvider = null;
  }

  // Allow app to register an external token provider (Keycloak)
  setTokenProvider(providerFn) {
    this.externalTokenProvider = typeof providerFn === 'function' ? providerFn : null;
  }

  // MODIFICADO - Solo obtener token de Keycloak via externalTokenProvider
  // Get current token, preferring external provider over configured token
  async getToken() {
    if (this.externalTokenProvider) {
      try {
        const token = await this.externalTokenProvider();
        if (token && String(token).trim() !== '') {
          return token;
        }
      } catch (e) {
        if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
          console.warn('⚠️ External token provider failed');
        }
      }
    }
    
    // COMENTADO - Solo usar token de Keycloak via externalTokenProvider, no getAuthToken
    // getAuthToken puede devolver una promesa (para client_credentials)
    // const authToken = getAuthToken();
    // 
    // // Si es una promesa, la esperamos
    // if (authToken && typeof authToken.then === 'function') {
    //   return await authToken;
    // }
    // 
    // // Si no es una promesa, lo devolvemos directamente
    // return authToken;
    
    // Si no hay externalTokenProvider, no hay token disponible
    if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
      console.error('❌ No token provider configured. Please ensure Keycloak authentication is working.');
    }
    return null;
  }

  // MODIFICADO - Solo usar token de Keycloak via externalTokenProvider
  // Check if token is available
  hasToken() {
    // COMENTADO - Solo usar token de Keycloak via externalTokenProvider, no hasValidToken
    // return hasValidToken();
    
    // Verificar si hay un externalTokenProvider configurado
    return this.externalTokenProvider !== null;
  }

  // Make HTTP request with token
  async request(endpoint, options = {}) {
    // soporta tanto token síncrono como asíncrono
    const token = await this.getToken();

    if (!token) {
      throw new Error('No authentication token available. Please configure token in API Configuration.');
    }

    const url = `${this.baseUrl}${endpoint}`;

    // Fusiona primero options y luego compone headers SIN pisar Authorization
    const config = {
      // método por defecto
      method: options.method || 'GET',
      // resto de opciones primero (para no machacar después)
      ...options,
      // headers: default → options.headers → Authorization (queda al final)
      headers: {
        ...(PROXY_CONFIG?.defaultHeaders || {}),
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
      // explícito para CORS con Bearer (sin cookies)
      mode: 'cors',
      credentials: 'omit',
    };

    // Si hay body y no se definió Content-Type, asumimos JSON, excepto cuando es FormData
    const isFormData = (typeof FormData !== 'undefined') && (config.body instanceof FormData);
    if (config.body && !('Content-Type' in config.headers) && !('content-type' in config.headers) && !isFormData) {
      config.headers['Content-Type'] = 'application/json';
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        
        // Try to parse JSON error response for better error messages
        let errorMessage = errorText;
        let errorData = null;
        try {
          const errorJson = JSON.parse(errorText);
          errorData = errorJson;
          if (errorJson.message) {
            errorMessage = errorJson.message;
          } else if (errorJson.error) {
            errorMessage = errorJson.error;
          } else if (errorJson.detail) {
            errorMessage = errorJson.detail;
          }
        } catch (e) {
          // If not JSON, use the raw text
          errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
        }
        
        // Create a more descriptive error message based on status code
        let descriptiveMessage = errorMessage;
        if (response.status === 400) {
          if (errorMessage.includes('validation') || errorMessage.includes('Invalid')) {
            descriptiveMessage = `Validation Error: ${errorMessage}`;
          } else if (errorMessage.includes('duplicate') || errorMessage.includes('already exists')) {
            descriptiveMessage = `Duplicate Entry: ${errorMessage}`;
          } else {
            descriptiveMessage = `Bad Request: ${errorMessage}`;
          }
        } else if (response.status === 401) {
          descriptiveMessage = 'Authentication required. Please log in again.';
        } else if (response.status === 403) {
          descriptiveMessage = 'Access denied. You do not have permission to perform this action.';
        } else if (response.status === 404) {
          descriptiveMessage = 'The requested resource was not found.';
        } else if (response.status === 409) {
          descriptiveMessage = `Conflict: ${errorMessage}`;
        } else if (response.status === 422) {
          descriptiveMessage = `Validation Error: ${errorMessage}`;
        } else if (response.status >= 500) {
          descriptiveMessage = 'Server error. Please try again later.';
        }
        
        const error = new Error(descriptiveMessage);
        error.status = response.status;
        error.originalMessage = errorMessage;
        error.response = { data: errorData };
        
        if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
          console.error(`❌ API Error [${endpoint}]:`, error);
        }
        throw error;
      }

      // Maneja 204 o respuestas no-JSON con gracia
      if (response.status === 204) return null;
      const ct = response.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        return await response.json();
      }
      return await response.text();
    } catch (error) {
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error(`💥 Network Error [${endpoint}]:`, error);
      }
      throw error;
    }
  }

  // GET request
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }

  // POST request
  async post(endpoint, data) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT request
  async put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // PATCH request
  async patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Industries API using centralized endpoints
  async getIndustries() {
    try {
      const endpoint = buildEndpointUrl('PARAMETERS', apiConfig.ENDPOINTS.PARAMETERS.industries);
      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to load industries: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🏭 Industries API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Business Structures API using centralized endpoints
  async getBusinessStructures() {
    try {
      const endpoint = buildEndpointUrl('PARAMETERS', apiConfig.ENDPOINTS.PARAMETERS.businessStructures);
      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to load business structures: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🏗️ Business Structures API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Monitoring Centres API using merchant-service endpoint
  async getMonitoringCentres() {
    try {
      const endpoint = buildEndpointUrl('MERCHANTS', '/monitoring-centers/legal-names');
      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to load monitoring centres: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🏢 Monitoring Centres API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Get monitoring center details by ID
  async getMonitoringCenterById(monitoringCenterId) {
    try {
      if (!monitoringCenterId) {
        throw new Error('Monitoring center ID is required');
      }
      
      const endpoint = buildEndpointUrl('MERCHANTS', `/${monitoringCenterId}`);
      
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🔍 Loading monitoring center details for ID: ${monitoringCenterId}`);
      }
      
      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to load monitoring center details: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🏢 Monitoring Center Details API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Get monitoring center details by legal name
  async getMonitoringCenterByName(legalName) {
    try {
      if (!legalName) {
        throw new Error('Legal name is required');
      }
      
      // Search for monitoring center by legal name using the filter endpoint
      const searchFilters = {
        legalName: {
          operation: 'EQUALS',
          value: legalName
        },
        type: {
          operation: 'EQUALS', 
          value: 'MONITORING_CENTER'
        }
      };
      
      const endpoint = buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.filter);
      const url = `${endpoint}?page=0&size=1`;
      
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🔍 Loading monitoring center details for: ${legalName}`);
      }
      
      const data = await this.post(url, searchFilters);
      
      // Return the first result if found
      if (data && data.content && data.content.length > 0) {
        return data.content[0];
      }
      
      return null;
    } catch (error) {
      const errorMsg = `Failed to load monitoring center details: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🏢 Monitoring Center Details API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Merchants list API using centralized endpoints with pagination validation
  async getMerchants(size = 10, page = 0, excludeMonitoringCenter = true) {
    try {
      // Validate size parameter (max 100)
      const validatedSize = Math.min(Math.max(1, size), 100);
      const validatedPage = Math.max(0, page);

      if (size > 100) {
        console.warn(`🚨 Size ${size} exceeds maximum (100), using 100 instead`);
      }

      // Build endpoint with excludeMonitoringCenter parameter
      let endpoint = buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.list(validatedSize, validatedPage));
      if (excludeMonitoringCenter) {
        endpoint += '&excludeMonitoringCenter=true';
      }

      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to load merchants: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🏢 Merchants API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Get merchants by type (DEALER, MONITORING_CENTER)
  async getMerchantsByType(type, size = 30, page = 0) {
    try {
      // Validate pagination parameters
      const validatedSize = Math.min(Math.max(1, size), 100);
      const validatedPage = Math.max(0, page);

      if (size > 100) {
        console.warn(`🚨 Size ${size} exceeds maximum (100), using 100 instead`);
      }

      const endpoint = buildEndpointUrl('MERCHANTS', `/types/${type}?size=${validatedSize}&page=${validatedPage}`);

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`📄 Loading merchants by type: ${type}, page=${validatedPage}, size=${validatedSize}`);
      }

      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to load merchants by type ${type}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🏢 Merchants by Type API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  async updateMerchant(merchantId, merchantPayload) {
    try {
      const endpoint = buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.update(merchantId));

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`✏️ Updating merchant ${merchantId}:`, merchantPayload);
      }

      const data = await this.post(endpoint, merchantPayload);
      return data;
    } catch (error) {
      const errorMsg = `Failed to update merchant ${merchantId}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('✏️ Update Merchant API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Helper function to convert old filter array format to MerchantFilterRequestDto
  convertLegacyFiltersToDto(legacyFilters = []) {
    console.log('🔄 Converting legacy filters to DTO:', legacyFilters);
    
    const merchantFilterRequestDto = {};
    
    // Map old field names to DTO field names
    const fieldMapping = {
      'legal_name': 'legalName',
      'dba_name': 'dbaName',
      'federal_tax_id': 'federalTaxId',
      'state_of_inc': 'stateOfInc',
      'industry': 'industry',
      'date_established': 'dateEstablished',
      'type': 'type'
    };
    
    // Map old operators to new FilterOperation enum
    const operatorMapping = {
      'ILIKE': 'CONTAINS',
      '=': 'EQUALS',
      'EQUALS': 'EQUALS',
      'CONTAINS': 'CONTAINS'
    };
    
    legacyFilters.forEach(filter => {
      if (filter.field && filter.value && filter.operator) {
        const dtoField = fieldMapping[filter.field];
        if (dtoField) {
          const operation = operatorMapping[filter.operator] || 'CONTAINS';
          // Remove % wildcards since CONTAINS operation handles that automatically
          const cleanValue = filter.value.replace(/^%|%$/g, '');
          
          merchantFilterRequestDto[dtoField] = {
            operation: operation,
            value: cleanValue
          };
          
          console.log(`✅ Converted legacy filter: ${filter.field} → ${dtoField} = ${JSON.stringify(merchantFilterRequestDto[dtoField])}`);
        }
      }
    });
    
    console.log('🎯 Converted DTO:', merchantFilterRequestDto);
    return merchantFilterRequestDto;
  }

  // Transform frontend filters to MerchantFilterRequestDto format
  transformFiltersToApi(frontendFilters) {
    console.log('🚀 transformFiltersToApi called with:', frontendFilters);

    const merchantFilterRequestDto = {};

    // Map frontend field names to DTO field names
    const fieldMapping = {
      legalName: 'legalName',
      dbaName: 'dbaName', 
      federalTaxId: 'federalTaxId',
      stateOfInc: 'stateOfInc',
      industry: 'industry',
      dateEstablished: 'dateEstablished',
      type: 'type'
    };

    // Map frontend operators to backend FilterOperation enum
    const operatorMapping = {
      'ILIKE': 'CONTAINS',
      '=': 'EQUALS',
      'EQUALS': 'EQUALS',
      'CONTAINS': 'CONTAINS'
    };

    // Extract main fields (excluding _operator fields)
    const mainFields = Object.keys(frontendFilters).filter(key => !key.endsWith('_operator'));

    console.log('📋 Main fields to process:', mainFields);

    // Process each main field
    mainFields.forEach(field => {
      const value = frontendFilters[field];
      const operatorKey = `${field}_operator`;
      const frontendOperator = frontendFilters[operatorKey] || 'ILIKE';

      console.log(`🔍 Processing field: ${field}, value: "${value}", operator: ${frontendOperator}`);

      if (value && value.trim() !== '') {
        const dtoField = fieldMapping[field];
        if (dtoField) {
          const backendOperation = operatorMapping[frontendOperator] || 'CONTAINS';
          const apiValue = value.trim();

          const filterCriteria = {
            operation: backendOperation,
            value: apiValue
          };

          merchantFilterRequestDto[dtoField] = filterCriteria;

          console.log(`✅ Added filter: ${dtoField} = ${JSON.stringify(filterCriteria)}`);
        } else {
          console.log(`❌ Skipping field ${field} - no mapping found`);
        }
      } else {
        console.log(`❌ Skipping field ${field} - empty or invalid value: "${value}"`);
      }
    });

    console.log('🎯 Final MerchantFilterRequestDto:', merchantFilterRequestDto);

    return merchantFilterRequestDto;
  }

  // Transform frontend filters to API format for applications
  transformApplicationFiltersToApi(frontendFilters) {
    const apiFilters = [];

    // Map frontend field names to API field names (matching application search endpoint)
    const fieldMapping = {
      event: 'event',
      status: 'status',
      createdBy: 'created_by'
    };

    // Extract main fields (excluding _operator fields)
    const mainFields = Object.keys(frontendFilters).filter(key => !key.endsWith('_operator'));

    // Process each main field
    mainFields.forEach(field => {
      const value = frontendFilters[field];
      const operatorKey = `${field}_operator`;
      const operator = frontendFilters[operatorKey] || 'ILIKE';

      if (value && value.trim() !== '') {
        const apiField = fieldMapping[field] || field;

        let apiValue = value.trim();

        // Apply value transformation based on operator
        if (operator === 'ILIKE') {
          // Add wildcards for contains search
          apiValue = `%${apiValue}%`;
        } else if (operator === '=') {
          // Use exact value for equals
        }

        // Convert status and event to uppercase for backend compatibility
        if (apiField === 'status' || apiField === 'event') {
          apiValue = apiValue.toUpperCase();
        }

        const filterObject = {
          field: apiField,
          operator: operator,
          value: apiValue,
        };

        apiFilters.push(filterObject);
      } else {
      }
    });

    return apiFilters;
  }

  // Simple merchant search API using GET with query parameter
  async searchMerchants(query = '', page = 0, size = 10) {
    try {
      // Validate pagination parameters (max size 100)
      const validatedSize = Math.min(Math.max(1, size), 100);
      const validatedPage = Math.max(0, page);

      if (size > 100) {
        console.warn(`🚨 Search size ${size} exceeds maximum (100), using 100 instead`);
      }

      const endpoint = buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.search);

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🔍 Simple searching merchants: query="${query}", page=${validatedPage}, size=${validatedSize}, excludeMonitoringCenter=true`);
      }

      const url = `${endpoint}?q=${encodeURIComponent(query)}&page=${validatedPage}&size=${validatedSize}&excludeMonitoringCenter=true`;
      const data = await this.get(url);
      return data;
    } catch (error) {
      const errorMsg = `Failed to search merchants: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🔍 Merchant Search API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Advanced merchant search API using POST with MerchantFilterRequestDto to filter endpoint
  async filterMerchants(merchantFilterRequestDto = {}, page = 0, size = 10) {
    try {
      // Validate pagination parameters (max size 100)
      const validatedSize = Math.min(Math.max(1, size), 100);
      const validatedPage = Math.max(0, page);

      if (size > 100) {
        console.warn(`🚨 Filter size ${size} exceeds maximum (100), using 100 instead`);
      }

      const endpoint = buildEndpointUrl('MERCHANTS', apiConfig.ENDPOINTS.MERCHANTS.filter);

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🔍 Advanced filtering merchants: page=${validatedPage}, size=${validatedSize}, excludeMonitoringCenter=true`);
        console.log(`📦 MerchantFilterRequestDto:`, merchantFilterRequestDto);
      }

      const url = `${endpoint}?page=${validatedPage}&size=${validatedSize}&excludeMonitoringCenter=true`;
      const data = await this.post(url, merchantFilterRequestDto);
      return data;
    } catch (error) {
      const errorMsg = `Failed to filter merchants: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🔍 Merchant Filter API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Notes API using centralized endpoints
  async getNotesByTag(tag) {
    try {
      const endpoint = buildEndpointUrl('NOTES', apiConfig.ENDPOINTS.NOTES.getByTag(tag));

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🏷️ Loading notes for tag: ${tag}`);
      }

      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to load notes for tag ${tag}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('📝 Notes API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  async createNote(noteData) {
    try {
      const endpoint = buildEndpointUrl('NOTES', apiConfig.ENDPOINTS.NOTES.create);

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`📝 Creating note:`, noteData);
      }

      const data = await this.post(endpoint, noteData);
      return data;
    } catch (error) {
      const errorMsg = `Failed to create note: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('📝 Create Note API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  async updateNote(noteId, noteData) {
    try {
      const endpoint = buildEndpointUrl('NOTES', apiConfig.ENDPOINTS.NOTES.update(noteId));

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`✏️ Updating note ${noteId}:`, noteData);
      }

      const data = await this.put(endpoint, noteData);
      return data;
    } catch (error) {
      const errorMsg = `Failed to update note ${noteId}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('✏️ Update Note API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  async softDeleteNote(noteId, active = false) {
    try {
      const endpoint = buildEndpointUrl('NOTES', apiConfig.ENDPOINTS.NOTES.softDelete(noteId));
      const payload = { active };

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🔄 Soft ${active ? 'activating' : 'deactivating'} note ${noteId}`);
      }

      const data = await this.patch(endpoint, payload);
      return data;
    } catch (error) {
      const errorMsg = `Failed to ${active ? 'activate' : 'deactivate'} note ${noteId}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🔄 Soft Delete Note API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  async deleteNote(noteId) {
    try {
      const endpoint = buildEndpointUrl('NOTES', apiConfig.ENDPOINTS.NOTES.delete(noteId));

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🗑️ Deleting note ${noteId}`);
      }

      const data = await this.delete(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to delete note ${noteId}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🗑️ Delete Note API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  async getNotesPaginated(page = 0, size = 10, sort = 'id,desc') {
    try {
      const endpoint = buildEndpointUrl('NOTES', apiConfig.ENDPOINTS.NOTES.list(page, size, sort));

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`📄 Loading notes page ${page}, size ${size}, sort ${sort}`);
      }

      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to load notes page ${page}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('📄 Notes Pagination API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  async searchNotes(searchCriteria = {}) {
    try {
      // Usar POST con body JSON en lugar de GET con query parameters
      const searchPath = apiConfig.ENDPOINTS.NOTES.search;
      const endpoint = buildEndpointUrl('NOTES', searchPath);

      // Preparar el body del request según el formato esperado por el backend
      const requestBody = {
        // Parámetros de paginación
        page: searchCriteria.page || 0,
        size: searchCriteria.size || 20,
        sort: searchCriteria.sort || 'createdDate,desc',
        
        // Filtros de búsqueda
        ...(searchCriteria.originType && { originType: searchCriteria.originType }),
        ...(searchCriteria.originId && { originId: searchCriteria.originId }),
        ...(searchCriteria.tag && { tag: searchCriteria.tag }),
        ...(searchCriteria.systemTag && { systemTag: searchCriteria.systemTag }),
        ...(searchCriteria.systemTags && { systemTags: searchCriteria.systemTags }),
        ...(searchCriteria.createdBy && { createdBy: searchCriteria.createdBy }),
        // Rango de fechas: aceptar tanto createdDateFrom/To como dateFrom/To
        ...(searchCriteria.createdDateFrom && { createdDateFrom: searchCriteria.createdDateFrom }),
        ...(searchCriteria.createdDateTo && { createdDateTo: searchCriteria.createdDateTo }),
        ...(searchCriteria.dateFrom && { dateFrom: searchCriteria.dateFrom }),
        ...(searchCriteria.dateTo && { dateTo: searchCriteria.dateTo }),
        // Mensaje: enviar 'message' y, por compatibilidad, popular 'search' si no viene
        ...(searchCriteria.message && { message: searchCriteria.message }),
        ...(searchCriteria.search && { search: searchCriteria.search }),
        ...(!searchCriteria.search && searchCriteria.message && { search: searchCriteria.message }),
        // Tags de usuario
        ...(searchCriteria.userTags && { userTags: searchCriteria.userTags }),
        ...(searchCriteria.active !== undefined && { active: searchCriteria.active })
      };

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🔍 Searching notes with criteria:`, searchCriteria);
        console.log(`🔍 Request body:`, requestBody);
        console.log(`🔍 Full endpoint URL:`, endpoint);
      }

      const data = await this.post(endpoint, requestBody);
      return data;
    } catch (error) {
      const errorMsg = `Failed to search notes: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🔍 Notes Search API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  async createApplication(applicationData, recaptchaToken = null, isExternalForm = false) {
    try {
      const endpoint = buildEndpointUrl(
        "APPLICATIONS",
        apiConfig.ENDPOINTS.APPLICATIONS.create
      );

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`📋 Creating application:`, applicationData);
        if (recaptchaToken) {
          console.log(`🔒 reCAPTCHA token provided for application creation`);
        }
      }

      // Prepare headers with reCAPTCHA token if provided
      const headers = {};
      if (recaptchaToken) {
        headers["recaptcha"] = recaptchaToken;
        headers["external"] = isExternalForm;
      }

      // Use the request method directly to include custom headers
      const data = await this.request(endpoint, {
        method: "POST",
        body: JSON.stringify(applicationData),
        headers,
      });
      return data;
    } catch (error) {
      const errorMsg = `Failed to create application: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error("📋 Create Application API Error:", error);
      }
      throw new Error(errorMsg);
    }
  }

  async updateApplication(applicationId, applicationData) {
    try {
      const endpoint = buildEndpointUrl('APPLICATIONS', apiConfig.ENDPOINTS.APPLICATIONS.update(applicationId));

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`✏️ Updating application ${applicationId}:`, applicationData);
      }

      const data = await this.post(endpoint, applicationData);
      return data;
    } catch (error) {
      const errorMsg = `Failed to update application ${applicationId}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('✏️ Update Application API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Applications list using standard page/size pagination
  async getApplications(page = 0, size = 10, excludeProcessed = true) {
    try {
      const validatedSize = Math.min(Math.max(1, size), 100);
      const validatedPage = Math.max(0, page);

      const qs = apiConfig.ENDPOINTS.APPLICATIONS.list(validatedSize, validatedPage);
      const endpoint = buildEndpointUrl('APPLICATIONS', qs);
      
      // Add excludeProcessed parameter if needed
      const finalEndpoint = excludeProcessed 
        ? `${endpoint}${endpoint.includes('?') ? '&' : '?'}excludeProcessed=true`
        : endpoint;

      const data = await this.get(finalEndpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to fetch applications: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('📋 Get Applications API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // General search for applications (text, ids, tax-id, etc.)
  async generalSearchApplication(searchValue = '', page = 0, size = 10) {
    try {
      const endpoint = buildEndpointUrl('APPLICATIONS', apiConfig.ENDPOINTS.APPLICATIONS.generalSearch(searchValue, page, size));

      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to general-search applications: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🔎 General Search Applications API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Get applications by status with pagination in last 24 hours (timezone-aware)
  async getApplicationsByStatus(status, page = 0, size = 10) {
    try {
      // Calculate timezone offset from client (same logic as CountApplicationsByStatus)
      const now = new Date();
      const offsetMinutes = now.getTimezoneOffset(); // minutes behind/ahead UTC
      const timezoneOffset = -offsetMinutes / 60; // hours, sign flipped

      const endpoint = buildEndpointUrl('APPLICATIONS', apiConfig.ENDPOINTS.APPLICATIONS.byStatusLast24Hours(status, timezoneOffset, page, size));

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`📄 Fetching applications by status ${status} (last 24 hours) with timezone offset ${timezoneOffset}`);
      }

      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to fetch applications by status ${status}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('📄 Applications By Status API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Get count by status
  async getCountByStatus(status) {
    try {
      const endpoint = buildEndpointUrl('APPLICATIONS', apiConfig.ENDPOINTS.APPLICATIONS.countByStatus(status));
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🔢 Applications count by status: ${status}`);
      }
      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to fetch applications count for ${status}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🔢 Applications Count By Status API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Get count by status using client timezone offset in last 24 hours
  async CountApplicationsByStatus(status) {
    try {
      const now = new Date();
      const offsetMinutes = now.getTimezoneOffset(); // minutes behind/ahead UTC
      const timezoneOffset = -offsetMinutes / 60; // hours, sign flipped

      // Use last-24-hours endpoint with minimal size to get count from totalElements
      const endpoint = buildEndpointUrl('APPLICATIONS', apiConfig.ENDPOINTS.APPLICATIONS.countByStatusLast24Hours(status, timezoneOffset));

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🔢 Counting applications by status ${status} (last 24 hours) with timezone offset ${timezoneOffset}`);
      }

      const data = await this.get(endpoint);
      // Extract totalElements from the paginated response (using size=1 to minimize payload)
      const count = data?.totalElements || data?.total || data || 0;
      return count;
    } catch (error) {
      const errorMsg = `Failed to fetch timezone-aware applications count for ${status}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🔢 Applications Count By Status (Client TZ) API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Advanced search for applications using the new endpoint
  async searchApplications(filters = [], page = 0, size = 10) {
    try {
      const endpoint = '/application-service/api/v2/applications/search';
      
      const payload = {
        page,
        size,
        filters
      };

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🔍 Searching applications with filters:`, payload);
      }

      const data = await this.post(endpoint, payload);
      return data;
    } catch (error) {
      const errorMsg = `Failed to search applications: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🔍 Search Applications API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // New advanced search payload style
  async advanceSearchApplications(payload = { page: 0, size: 10 }) {
    try {
      const endpoint = buildEndpointUrl('APPLICATIONS', apiConfig.ENDPOINTS.APPLICATIONS.filter);

      const data = await this.post(endpoint, payload);
      return data;
    } catch (error) {
      const errorMsg = `Failed to advance-filter applications: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🔎 Advance Filter Applications API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  async getApplicationById(applicationId) {
    try {
      const endpoint = buildEndpointUrl('APPLICATIONS', apiConfig.ENDPOINTS.APPLICATIONS.getById(applicationId));

      const data = await this.get(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to fetch application details: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('📋 Get Application Details API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  async deleteApplication(applicationId, merchantId) {
    try {
      const endpoint = buildEndpointUrl('APPLICATIONS', apiConfig.ENDPOINTS.APPLICATIONS.delete(applicationId, merchantId));

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🗑️ Deleting application ${applicationId} with merchantId=${merchantId}`);
      }

      const data = await this.delete(endpoint);
      return data;
    } catch (error) {
      const errorMsg = `Failed to delete application ${applicationId}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🗑️ Delete Application API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  async createDeal(dealPayload) {
    try {
      const endpoint = buildEndpointUrl('DEALS', apiConfig.ENDPOINTS.DEALS.create);

      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log('🚀 Creating deal with payload:', dealPayload);
      }

      const data = await this.post(endpoint, dealPayload);
      return data;
    } catch (error) {
      const errorMsg = `Failed to create deal: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🤝 Create Deal API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Promote application to deal by ID (no payload)
  async promoteToDeal(applicationId) {
    try {
      const endpoint = buildEndpointUrl('APPLICATIONS', apiConfig.ENDPOINTS.APPLICATIONS.process(applicationId));
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
        console.log(`🚀 Promote application ${applicationId} to deal`);
      }
      const data = await this.post(endpoint, {});
      return data;
    } catch (error) {
      const errorMsg = `Failed to promote application ${applicationId}: ${error.message}`;
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error('🚀 Promote To Deal API Error:', error);
      }
      throw new Error(errorMsg);
    }
  }

  // Request especial para endpoints que devuelven blobs y necesitan acceso a headers HTTP
  // Útil para preview de documentos, descargas, etc.
  async requestWithBlob(endpoint, options = {}) {
    const token = await this.getToken();

    if (!token) {
      throw new Error('No authentication token available. Please configure token in API Configuration.');
    }

    // Construir URL absoluta usando base del API para evitar que el navegador resuelva contra la SPA
    const rel = buildEndpointUrl('DOCUMENTS', endpoint);
    const url = `${appConfig.baseUrl}${rel}`;

    // Configuración para fetch con headers personalizados
    const config = {
      method: options.method || 'GET',
      headers: {
        ...(PROXY_CONFIG?.defaultHeaders || {}),
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
      mode: 'cors',
      credentials: 'omit',
      redirect: options.redirect || 'manual', // Permitir manejar redirects manualmente
      signal: options.signal,
    };

    // Log request if enabled
    if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showRequests) {
      console.log(`📤 Blob API Request: ${config.method} ${url}`);
    }

    try {
      const response = await fetch(url, config);

      // Log response if enabled
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showResponses) {
        console.log(`📥 Blob API Response: ${response.status} ${response.statusText}`);
      }

      // Retornar la respuesta completa para que el caller pueda procesarla
      return response;
    } catch (error) {
      if (LOGGING_CONFIG.enabled && LOGGING_CONFIG.showErrors) {
        console.error(`💥 Blob Network Error [${endpoint}]:`, error);
      }
      throw error;
    }
  }
}

// Create singleton instance
const apiService = new ApiService();

// Export both the class and the instance
export { ApiService };
export default apiService;
